import { useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { Feature } from 'player'
import { Task } from '../../../types'
import { useAddLyrics } from '../../../lib/operations/creation'
import { useFetchLyrics } from '../../tasks/use-fetch-lyrics'
import { useStorePlayer } from '../../../modules/player/player-module/store'
import { trigger } from '../../../lib/events'

interface UseLyrics {
  onGoEditorLyrics(): void
  onAddLyrics(params: { language?: string }): void
}

interface UseLyricsProps {
  trackId: string
  metadata: any
  isPremium?: boolean
  onLockedFeatureClick?(feature: Feature): void
  task: Task
}

export const useLyrics = ({
  trackId,
  metadata,
  isPremium,
  onLockedFeatureClick,
  task
}: UseLyricsProps): UseLyrics => {
  const router = useRouter()
  const { setData } = useStorePlayer()
  const { lyrics, separate } = task?.summary?.v1

  const lyricsStatus = useMemo(() => lyrics?.status, [lyrics])
  const lyricsUpdateInProgress = useMemo(
    () => !!lyrics?.updateInProgress,
    [lyrics]
  )
  const vocalsStem = useMemo(
    () => separate?.stems?.find((stem) => stem?.stem === 'vocals'),
    [separate]
  )
  const lyricsParams = useMemo(() => vocalsStem?.path, [vocalsStem])

  const {
    lyrics: lyricsData,
    error,
    loading
  } = useFetchLyrics(
    lyrics?.lyrics?.path,
    metadata?.find((item: any) => item.key === 'lyrics')?.value
  )

  useEffect(() => {
    const lyricsAux: any = {
      data: lyricsData,
      availableLanguages: [],
      empty: !lyricsData?.length,
      blocked: !lyricsParams && !lyricsData?.length,
      status: lyricsUpdateInProgress
        ? 'update'
        : error || lyricsStatus === 'FAILED'
        ? 'failed'
        : lyricsStatus === 'QUEUED' || lyricsStatus === 'STARTED'
        ? 'processing'
        : lyricsData?.length
        ? 'succeeded'
        : undefined
    }

    setData({ lyrics: lyricsAux })
  }, [
    setData,
    lyricsData,
    loading,
    error,
    lyricsStatus,
    lyricsParams,
    lyricsUpdateInProgress
  ])

  const onGoEditorLyrics = useCallback(() => {
    if (!isPremium) {
      onLockedFeatureClick?.('lyrics-edit-lock')
      return
    }

    trigger('player:pause-media')

    const separateId =
      vocalsStem?.path?.split('/')?.reverse()[1] ||
      lyrics?.lyrics?.path?.split('/')?.reverse()[1]

    const separateType = lyricsParams?.split('/').reverse()[2]
    const separateOp = separateType?.split('_')[1]

    router.push(`/editor/lyrics/${trackId}/${separateId}/${separateOp}/`)
  }, [
    lyricsParams,
    isPremium,
    vocalsStem,
    lyrics,
    router,
    trackId,
    onLockedFeatureClick
  ])

  const onAddLyrics = useAddLyrics(trackId, {
    vocalsPath: lyricsParams || ''
  })

  return {
    onAddLyrics,
    onGoEditorLyrics
  }
}
